import { useEffect, useState, useCallback, useRef } from "react";
import { MessageCircle } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { openChavrutaWithContext } from "./ChavrutaBubble";

/**
 * TextSelectionMenu — a global floating "Ask Chavruta" button that
 * appears whenever the user selects text inside a Sefaria-anchored
 * element.
 *
 * How it knows what verse the selection belongs to
 * ------------------------------------------------
 * The Tanakh `BookPage` and the Siddur `SiddurPage` / scroll `Subsection`
 * components each add `data-ref="<Sefaria ref>"` (e.g. `Genesis 1:3`,
 * `Siddur Ashkenaz, Weekday, Shacharit, Preparatory Prayers, Modeh Ani`)
 * to their unit wrappers. When a user selects text, we walk up the
 * DOM from the selection's anchor node to the nearest ancestor
 * carrying `data-ref` and use that as the canonical reference.
 *
 * Why a global menu (not per-page popovers)
 * -----------------------------------------
 *   • One implementation handles Tanakh + Siddur + future readers
 *     (Mishnah, Zohar) without duplicating logic.
 *   • The browser only has one active selection at a time, so a
 *     singleton observer is the natural shape.
 *   • Mounting at the App layer means the button can position
 *     itself in viewport coordinates without worrying about page
 *     transforms (the flipbook in particular has 3D transforms
 *     that would break a child popover).
 *
 * Mobile considerations
 * ---------------------
 * On iOS the native "Look Up / Share" callout appears on selection
 * and can overlap our button. We position the button BELOW the
 * selection (not above) and add a 12px safety gap. On both iOS and
 * Android we hide the button when the selection is empty/collapsed,
 * matching the platform's own selection callout behavior.
 *
 * The component is hidden entirely for anonymous visitors, matching
 * the bubble's visibility rule — the chavruta endpoints require auth.
 */
const MIN_SELECTION_LEN = 2;       // Avoid showing on accidental single-char taps
const VERTICAL_GAP_PX = 12;        // Distance below the selection rect

export default function TextSelectionMenu() {
  const { user } = useAuth();
  const [target, setTarget] = useState(null);
  const armedRef = useRef(false);   // True between pointerdown and pointerup
  // While dragging a selection we shouldn't try to position the
  // button — wait for the pointer-up that ends the drag. Otherwise
  // the button flickers mid-selection.

  const computeTarget = useCallback(() => {
    if (typeof window === "undefined") return null;
    const sel = window.getSelection();
    if (!sel || sel.isCollapsed) return null;
    const text = sel.toString().trim();
    if (text.length < MIN_SELECTION_LEN) return null;

    // Walk up from the selection's anchor (or focus) to find the
    // nearest ancestor with `data-ref`. We try both anchorNode and
    // focusNode because a selection that crosses elements might
    // start in one and end in another — taking the focusNode picks
    // the verse the user STOPPED on, which usually maps better to
    // intent.
    const node = sel.focusNode || sel.anchorNode;
    if (!node) return null;
    let el = node.nodeType === 1 ? node : node.parentElement;
    let ref = null;
    while (el && el !== document.body) {
      if (el.dataset && el.dataset.ref) {
        ref = el.dataset.ref;
        break;
      }
      el = el.parentElement;
    }
    if (!ref) return null;

    // Position: viewport coords of the selection rect.
    let rect;
    try {
      const range = sel.getRangeAt(0);
      rect = range.getBoundingClientRect();
    } catch {
      return null;
    }
    if (!rect || (rect.width === 0 && rect.height === 0)) return null;

    return {
      ref,
      text,
      // Center horizontally on the selection; clamp at viewport
      // edges with a 12px margin.
      x: Math.min(
        Math.max(12, rect.left + rect.width / 2),
        window.innerWidth - 12,
      ),
      // Below the selection. iOS callout is normally ABOVE, so
      // below avoids overlap.
      y: rect.bottom + VERTICAL_GAP_PX,
    };
  }, []);

  // Selection change listener — fires constantly during drag, so we
  // gate updates on pointer-up to avoid flicker on each character.
  useEffect(() => {
    if (!user) return;
    const onChange = () => {
      if (armedRef.current) return; // still dragging
      const t = computeTarget();
      setTarget(t);
    };
    const onPointerDown = (e) => {
      // If the pointerdown is on the ASK button itself (or its
      // descendants), don't reset state — otherwise the global
      // listener removes the menu from the DOM before the click
      // handler ever fires, and the panel never opens. We detect
      // this via the data-testid set on the button.
      const t = e.target;
      if (t && t.closest && t.closest('[data-testid="text-selection-ask-chavruta"]')) {
        return;
      }
      armedRef.current = true;
      setTarget(null);
    };
    const onPointerUp = () => {
      armedRef.current = false;
      // Defer a frame so getSelection() reflects the final state.
      requestAnimationFrame(() => {
        const t = computeTarget();
        setTarget(t);
      });
    };
    document.addEventListener("selectionchange", onChange);
    // Use capture phase: the Tanakh BookPage and Siddur subsections
    // each call `stopPropagation()` on pointerdown to prevent the
    // react-pageflip library from interpreting a text drag as a
    // page swipe. Without `capture: true` here, the same stop also
    // prevents OUR listener from firing, and the menu would never
    // unarm.
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);
    document.addEventListener("touchend", onPointerUp, true);
    // Hide on scroll/resize so the button doesn't end up floating
    // over the wrong line.
    const onLayout = () => setTarget(null);
    window.addEventListener("scroll", onLayout, true);
    window.addEventListener("resize", onLayout);
    return () => {
      document.removeEventListener("selectionchange", onChange);
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("touchend", onPointerUp, true);
      window.removeEventListener("scroll", onLayout, true);
      window.removeEventListener("resize", onLayout);
    };
  }, [user, computeTarget]);

  if (!user || !target) return null;

  const handleAsk = (e) => {
    // Stop the click from killing the selection — and from being
    // interpreted as a swipe by the flipbook.
    e.preventDefault();
    e.stopPropagation();
    const { ref, text } = target;
    setTarget(null);
    // Clear the native selection so the callout disappears under
    // the panel that's about to open.
    try { window.getSelection()?.removeAllRanges(); } catch {}
    openChavrutaWithContext({
      ref,
      selected_text: text,
    });
  };

  return (
    <button
      type="button"
      onMouseDown={(e) => e.preventDefault()} // keep selection alive on press
      onClick={handleAsk}
      data-testid="text-selection-ask-chavruta"
      aria-label="למד בחברותא על הטקסט המסומן"
      style={{
        position: "fixed",
        left: target.x,
        top: target.y,
        transform: "translateX(-50%)",
        zIndex: 60,
      }}
      className="
        inline-flex items-center gap-1.5
        h-9 px-3.5
        rounded-full
        bg-[#1a2b4a] text-[#f6e9c5]
        border border-[#c9a449]/70
        shadow-[0_6px_20px_-6px_rgba(26,43,74,0.55)]
        hover:shadow-[0_10px_24px_-6px_rgba(201,164,73,0.4)]
        active:scale-95
        transition-all duration-150
        text-[12px] font-heading
        whitespace-nowrap
      "
      dir="rtl"
    >
      <MessageCircle className="h-3.5 w-3.5 text-[#c9a449]" strokeWidth={1.8} />
      למד בחברותא
    </button>
  );
}
